export const articles = {
  'sleep-stages': {
    title: "Understanding Sleep Stages",
    content: `Sleep is not a uniform state but rather a dynamic process consisting of multiple stages, each serving unique biological functions essential for our physical and mental well-being.

    Light Sleep (N1 & N2):
    The initial stages of sleep where your body begins to relax. During these stages, your heart rate slows, body temperature drops, and you may experience occasional muscle twitches. Light sleep is important for memory consolidation and mental recovery.

    Deep Sleep (N3):
    Also known as slow-wave sleep, this stage is crucial for physical recovery. During deep sleep, your body releases growth hormone, repairs tissues, strengthens the immune system, and builds bone and muscle. The brain also clears out metabolic waste during this time.

    REM Sleep:
    The stage where most dreaming occurs. REM sleep is vital for emotional regulation, memory processing, and learning. Your brain is highly active during this stage, almost as active as when you're awake. This stage is particularly important for creativity and problem-solving abilities.

    The Science Behind Sleep Cycles:
    A typical sleep cycle lasts about 90-120 minutes, and you go through 4-6 cycles per night. The distribution of these stages changes throughout the night, with more deep sleep in the earlier cycles and more REM sleep in the later ones.

    Optimizing Your Sleep Architecture:
    • Maintain a consistent sleep schedule
    • Create a dark, cool sleeping environment
    • Avoid screens before bedtime
    • Limit caffeine and alcohol
    • Exercise regularly, but not too close to bedtime

    Understanding your sleep architecture helps you make informed decisions about your sleep habits and optimize your recovery process.`,
    readTime: "4 min"
  },
  'strain-calculation': {
    title: "How TODAY Move Calculates Strain",
    content: `TODAY Move uses a sophisticated algorithm to calculate your daily strain score, taking into account various physiological metrics and activity data to provide a comprehensive measure of your body's exertion.

    The Strain Algorithm:
    Our strain calculation considers multiple factors:
    • Cardiovascular Load: Based on heart rate and heart rate variability
    • Duration of Activity: Time spent in different heart rate zones
    • Activity Intensity: Impact of different types of activities
    • Recovery Status: Your body's readiness to handle strain

    Heart Rate Zones:
    We analyze time spent in different heart rate zones:
    • Zone 1 (50-60% max HR): Recovery
    • Zone 2 (60-70% max HR): Base fitness
    • Zone 3 (70-80% max HR): Aerobic endurance
    • Zone 4 (80-90% max HR): High intensity
    • Zone 5 (90-100% max HR): Maximum effort

    Strain Scale:
    The strain score ranges from 0-21:
    • 0-8: Light strain
    • 8-14: Moderate strain
    • 14-18: High strain
    • 18-21: Peak strain

    Real-time Calculation:
    Your strain score updates in real-time as you:
    • Complete workouts
    • Move throughout the day
    • Engage in various activities

    This dynamic calculation helps you understand your body's response to different activities and make informed decisions about your training intensity.`,
    readTime: "5 min"
  },
  'recovery-science': {
    title: "The Science of Recovery",
    content: `Recovery is a complex biological process that involves multiple systems in your body working together to restore and adapt to the stresses of training and daily life.

    Understanding Recovery:
    Recovery encompasses several key physiological processes:
    • Muscle tissue repair and growth
    • Nervous system restoration
    • Energy system replenishment
    • Hormonal balance restoration
    • Mental and emotional recovery

    Key Recovery Metrics:
    TODAY Move tracks several vital metrics to assess your recovery:
    
    1. Heart Rate Variability (HRV):
    • Primary indicator of autonomic nervous system balance
    • Higher HRV generally indicates better recovery
    • Influenced by stress, sleep, and training load

    2. Resting Heart Rate:
    • Lower resting heart rate often indicates better cardiovascular fitness
    • Daily fluctuations can indicate recovery status
    • Affected by factors like hydration and fatigue

    3. Sleep Quality:
    • Essential for physical and mental recovery
    • Deep sleep promotes tissue repair
    • REM sleep aids cognitive recovery

    4. Respiratory Rate:
    • Can indicate stress and recovery status
    • Lower rates typically suggest better recovery
    • Affected by training intensity and fatigue

    Optimizing Recovery:
    Evidence-based strategies for enhancing recovery:
    • Prioritize sleep quality and quantity
    • Practice stress management techniques
    • Maintain proper nutrition and hydration
    • Use active recovery methods
    • Balance training intensity with rest

    Recovery is not just about rest—it's an active process that requires attention and optimization for peak performance.`,
    readTime: "5 min"
  },
  'readiness-science': {
    title: "Understanding Readiness",
    content: `Your daily readiness score is a sophisticated measure that combines multiple physiological markers to assess your body's preparedness for performance and activity.

    Key Components of Readiness:
    1. Recovery Status
    • HRV trends and patterns
    • Autonomic nervous system balance
    • Cardiovascular recovery indicators

    2. Sleep Quality
    • Deep sleep duration
    • REM sleep patterns
    • Sleep consistency
    • Recovery optimization

    3. Physical Preparedness
    • Muscular recovery
    • Energy system restoration
    • Training load balance

    4. Mental Readiness
    • Cognitive function markers
    • Stress levels
    • Mental fatigue indicators

    How Readiness is Calculated:
    Your readiness score integrates multiple data points:
    • Previous day's strain
    • Sleep quality and quantity
    • HRV trends
    • Resting heart rate
    • Recent training load
    • Recovery patterns

    Optimizing Your Readiness:
    • Maintain consistent sleep schedule
    • Balance training intensity
    • Practice stress management
    • Monitor nutrition and hydration
    • Use active recovery techniques

    Understanding your readiness score helps you make informed decisions about training intensity and daily activity levels to optimize performance and prevent overtraining.`,
    readTime: "4 min"
  },
  'hrv-science': {
    title: "Understanding HRV",
    content: `Heart Rate Variability (HRV) is a powerful indicator of your body's autonomic nervous system balance and overall health status.

    What is HRV?
    HRV measures the variation in time between successive heartbeats. This variation is controlled by your autonomic nervous system and reflects your body's ability to adapt to different situations.

    Key Components:
    1. Parasympathetic Nervous System (Rest & Digest)
    • Increases HRV
    • Promotes recovery
    • Activated during rest and relaxation

    2. Sympathetic Nervous System (Fight or Flight)
    • Decreases HRV
    • Responds to stress
    • Activated during exercise and stress

    Factors Affecting HRV:
    • Sleep quality
    • Training load
    • Stress levels
    • Nutrition
    • Hydration
    • Recovery status
    • Age and fitness level

    Interpreting HRV:
    • Higher HRV generally indicates better recovery and adaptability
    • Lower HRV may suggest stress or incomplete recovery
    • Daily fluctuations are normal and expected

    Using HRV for Training:
    • Guide training intensity decisions
    • Optimize recovery periods
    • Monitor adaptation to training
    • Prevent overtraining

    Improving Your HRV:
    • Prioritize quality sleep
    • Practice stress management
    • Maintain consistent training
    • Focus on proper nutrition
    • Stay well hydrated
    • Use breathing exercises

    Regular HRV monitoring provides valuable insights into your body's stress and recovery balance, helping you optimize your training and lifestyle choices.`,
    readTime: "5 min"
  }
};

export const videos = {
  'strain-explained': {
    title: "The Science of Strain",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    duration: "12:45"
  }
};